import { birthday } from "src/calculator/utils";
import config from "src/services/config";
import { Person, PrintClient, ScenarioSettings } from "src/types";
import Timeline from "../Charts/Timeline";
import { useUser } from "src/hooks/useUser";
import { calculateAge } from "../Info/PersonInfo";

const PrintCard = ({ title, subtitle }: any) => (
  <div className="bg-gray-100 py-2 px-2 rounded-lg">
    <div className="uppercase text-gray-500 font-semibold text-xs mb-1 tracking-wider">
      {title}
    </div>
    <div className="font-semibold text-">{subtitle}</div>
  </div>
);

interface CoverProps {
  scenario: ScenarioSettings;
  client: PrintClient;
}

export const deathText = (person: Person, deathAge: number | null) => {
  if (!deathAge) return "";
  const { birthYear } = birthday(person);
  return `${deathAge} (${birthYear + deathAge})`;
};

const Cover = ({ scenario, client }: CoverProps) => {
  if (!client || !scenario) return null;
  const today = new Date().getFullYear();
  const { user } = useUser();

  let events = [
    {
      title: "Today",
      subtitle: "Current Date",
      year: today,
      id: "start",
    },
    {
      title: "End of Plan",
      subtitle: "Current Date",
      year: today + scenario.maxYearsShown,
      id: "end",
    },
  ];
  if (scenario.whoDies != -1) {
    const person = client.people[scenario.whoDies];
    const { birthYear } = birthday(person);
    const deathYear = scenario.deathYears[scenario.whoDies];
    if (deathYear)
      events.push({
        title: `${client.people[scenario.whoDies].name}'s Death`,
        subtitle: `Age: ${scenario.deathYears[scenario.whoDies]}`,
        year: deathYear + birthYear,
        id: `death-${scenario.whoDies}`,
      });
  }
  for (let i = 0; i < client.people.length; i++) {
    if (scenario.retirementYears[i]) {
      const person = client.people[i];
      const { birthYear } = birthday(person);
      const retirementYear = scenario.retirementYears[i];
      if (retirementYear)
        events.push({
          title: `${person.name}'s Retirement`,
          subtitle: `Age: ${scenario.retirementYears[i]}`,
          year: retirementYear + birthYear,
          id: `retirement-${i}`,
        });
    }
  }

  events = events.sort((a, b) => a.year - b.year);
  return (
    <div className="h-full  px-10 ">
      <div className="w-full">
        <div className="flex gap-2 items-center">
          <img
            src={
              client?.userdata?.logo
                ? `${config.PRINT_API_URL}logo?logo=${client?.userdata?.logo}`
                : "/img/logo.png"
            }
            alt="logo"
            className="h-16"
          />
          <div className="font-light text-xl">{client?.userdata.firmName}</div>
        </div>
        <div className="flex flex-col items-center gap-3">
          <div className="text-[2.2rem]">{scenario.name}</div>
          <div className="w-[100px] h-[2px] bg-main-orange"></div>
          <div className="text-gray-700">{client?.title}</div>
          <div className="flex gap-1 text-main-orange font-semibold mt-[-10px]">
            {client.people.map((person, index) => {
              const age = calculateAge(new Date(person.birthday));
              return (
                <div>
                  {person.name} ({age})
                  {index < client.people.length - 1 && " | "}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex justify-center w-full mt-16">
            <Timeline events={events} />
          </div>

          <div className="flex justify-between gap-10">
            <div className="bg-gray-100 py-3 px-3 rounded-r-lg  border border-l-main-orange border-l-8 w-full">
              <div className="text-main-orange uppercase text-sm font-semibold tracking-wide ml-2 mb-3">
                Scenario Assumptions
              </div>
              <div className="grid grid-cols-2 gap-3">
                <PrintCard
                  title="Inflation-Adjustment"
                  subtitle={`${scenario.inflationType === "Real" ? scenario.inflation || 0 : 0}%`}
                />
                {user?.info?.needsFlag && client.spending?.preTaxRate ? (
                  <PrintCard
                    title={`Pre-Retirement Tax Rate`}
                    subtitle={`${client.spending.preTaxRate}%`}
                  />
                ) : null}
                {user?.info?.needsFlag && client.spending?.postTaxRate ? (
                  <PrintCard
                    title={`Post-Retirement Tax Rate`}
                    subtitle={`${client.spending.postTaxRate}%`}
                  />
                ) : null}
              </div>
            </div>

            <div className="bg-gray-100 py-3 px-3 rounded-r-lg  border border-l-main-orange border-l-8 w-full">
              <div className="text-main-orange uppercase text-sm font-semibold tracking-wide mb-3">
                Advisor Information
              </div>
              <div className="flex flex-col gap-4">
                <div className="text-sm">
                  Prepared by{" "}
                  <span className="font-semibold">{client?.userdata.name}</span>{" "}
                  on <b>{new Date().toLocaleDateString()}</b>
                </div>
                {client?.userdata.disclosures && (
                  <div className="text-xs italic">
                    <b>Disclosures: </b>
                    {client.userdata.disclosures}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cover;
